/**
 * Documents Controller
 *
 * Manages state and gateway RPC calls for document knowledge base administration.
 */

import type {
  DocumentEntry,
  DocumentSearchResult,
  DocumentsAdminProps,
} from "../views/documents-admin.ts";

// --------------- State ---------------

export interface DocumentsState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  igProjects: Array<{ id: string; name: string; slug?: string }>;

  docsEntries: DocumentEntry[];
  docsLoading: boolean;
  docsError: string | null;
  docsFilterProjectId: string | null;
  docsSearchQuery: string;
  docsSearchResults: DocumentSearchResult[];
  docsSearching: boolean;
  docsUploading: boolean;
  docsReadFilename: string | null;
  docsReadText: string | null;
  docsReadLoading: boolean;
}

export const DOCS_STATE_DEFAULTS: Omit<DocumentsState, "client" | "connected" | "igProjects"> = {
  docsEntries: [],
  docsLoading: false,
  docsError: null,
  docsFilterProjectId: null,
  docsSearchQuery: "",
  docsSearchResults: [],
  docsSearching: false,
  docsUploading: false,
  docsReadFilename: null,
  docsReadText: null,
  docsReadLoading: false,
};

// --------------- Helpers ---------------

function friendlyError(err: unknown): string {
  const msg = String(err).toLowerCase();
  if (msg.includes("method not found") || msg.includes("not registered")) {
    return "Document methods not available. Ensure the igloobot-core plugin is loaded with enableDocuments.";
  }
  return String(err);
}

type ListResult = { ok: boolean; data?: DocumentEntry[]; error?: string };
type UploadResult = {
  ok: boolean;
  data?: { filename: string; format: string; chunks: number };
  error?: string;
};
type SearchResult = { ok: boolean; data?: DocumentSearchResult[]; error?: string };
type ReadResult = {
  ok: boolean;
  data?: {
    filename: string;
    format: string;
    totalChunks: number;
    chunks: Array<{ index: number; text: string }>;
  };
  error?: string;
};
type SimpleResult = { ok: boolean; error?: string };

// --------------- CRUD ---------------

export async function loadDocuments(state: DocumentsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.docsLoading = true;
  state.docsError = null;
  try {
    const params: Record<string, unknown> = {};
    if (state.docsFilterProjectId) {
      params.projectId = state.docsFilterProjectId;
    }
    const res = await state.client.request<ListResult>("igloobot.documents.list", params);
    if (res.ok && res.data) {
      state.docsEntries = res.data;
    } else {
      state.docsError = res.error ?? "Failed to load documents";
    }
  } catch (err) {
    state.docsError = friendlyError(err);
  } finally {
    state.docsLoading = false;
  }
}

export async function uploadDocumentFile(
  state: DocumentsState,
  file: File,
  projectId: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.docsUploading = true;
  state.docsError = null;
  try {
    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    // Convert to base64
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    const contentBase64 = btoa(binary);

    const res = await state.client.request<UploadResult>("igloobot.documents.upload", {
      projectId,
      filename: file.name,
      contentBase64,
    });
    if (res.ok) {
      await loadDocuments(state);
    } else {
      state.docsError = res.error ?? "Failed to upload document";
    }
  } catch (err) {
    state.docsError = friendlyError(err);
  } finally {
    state.docsUploading = false;
  }
}

export async function searchDocuments(state: DocumentsState): Promise<void> {
  if (!state.client || !state.connected || !state.docsSearchQuery.trim()) {
    return;
  }
  const projectId = state.docsFilterProjectId ?? state.igProjects[0]?.id;
  if (!projectId) {
    state.docsError = "Select a project to search.";
    return;
  }
  state.docsSearching = true;
  state.docsError = null;
  try {
    const res = await state.client.request<SearchResult>("igloobot.documents.search", {
      projectId,
      query: state.docsSearchQuery,
      limit: 10,
    });
    if (res.ok && res.data) {
      state.docsSearchResults = res.data;
    } else {
      state.docsError = res.error ?? "Search failed";
    }
  } catch (err) {
    state.docsError = friendlyError(err);
  } finally {
    state.docsSearching = false;
  }
}

export async function readDocumentContent(
  state: DocumentsState,
  projectId: string,
  filename: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.docsReadLoading = true;
  state.docsReadFilename = filename;
  state.docsReadText = null;
  state.docsError = null;
  try {
    const res = await state.client.request<ReadResult>("igloobot.documents.read", {
      projectId,
      filename,
    });
    if (res.ok && res.data) {
      state.docsReadText = res.data.chunks.map((c) => c.text).join("\n\n---\n\n");
    } else {
      state.docsError = res.error ?? "Failed to read document";
    }
  } catch (err) {
    state.docsError = friendlyError(err);
  } finally {
    state.docsReadLoading = false;
  }
}

export async function deleteDocument(
  state: DocumentsState,
  projectId: string,
  filename: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<SimpleResult>("igloobot.documents.delete", {
      projectId,
      filename,
    });
    if (res.ok) {
      await loadDocuments(state);
      // Clear read panel if we deleted the document being viewed
      if (state.docsReadFilename === filename) {
        state.docsReadFilename = null;
        state.docsReadText = null;
      }
    } else {
      state.docsError = res.error ?? "Failed to delete document";
    }
  } catch (err) {
    state.docsError = friendlyError(err);
  }
}

// --------------- Props builder ---------------

export function buildDocumentsProps(state: DocumentsState): DocumentsAdminProps {
  return {
    documents: state.docsEntries,
    loading: state.docsLoading,
    uploading: state.docsUploading,
    error: state.docsError,
    filterProjectId: state.docsFilterProjectId,
    projects: state.igProjects,
    searchQuery: state.docsSearchQuery,
    searchResults: state.docsSearchResults,
    searching: state.docsSearching,
    readFilename: state.docsReadFilename,
    readText: state.docsReadText,
    readLoading: state.docsReadLoading,
    onRefresh: () => void loadDocuments(state),
    onUpload: (file: File, projectId: string) => void uploadDocumentFile(state, file, projectId),
    onSearch: () => void searchDocuments(state),
    onSearchQueryChange: (query: string) => {
      state.docsSearchQuery = query;
    },
    onRead: (projectId: string, filename: string) =>
      void readDocumentContent(state, projectId, filename),
    onDelete: (projectId: string, filename: string) => {
      if (confirm(`Delete "${filename}"? This removes all chunks and cannot be undone.`)) {
        void deleteDocument(state, projectId, filename);
      }
    },
    onFilterChange: (projectId: string | null) => {
      state.docsFilterProjectId = projectId;
      void loadDocuments(state);
    },
    onCloseRead: () => {
      state.docsReadFilename = null;
      state.docsReadText = null;
    },
  };
}
